import { useRef, useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const
const cfTurnstileKey = process.env.NEXT_PUBLIC_MSL_CF_TURNSTILE_KEY

const highlightedFeatures = [
  'Identity & Access Management',
  'Single Sign-On',
  'Multi-Factor Authentication',
  'User Provisioning',
  'Privileged Access',
  'Identity Governance',
]

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  domain: '',
  domainSuffix: 'monofor.com',
}

if (process.env.NODE_ENV === 'development') {
  defaultValues.firstName = 'Selcuk'
  defaultValues.lastName = 'Doe'
  defaultValues.email = 'selcuk.doe@mono.directory'
  defaultValues.phone = '+14158004925'
  defaultValues.domain = 'mono'
}

type ResponseType = 'success' | 'failure' | 'none'

export default function TryNowSection() {
  const router = useRouter()

  const firstNameField = useRef<HTMLInputElement>(null)
  const lastNameField = useRef<HTMLInputElement>(null)
  const emailField = useRef<HTMLInputElement>(null)
  const phoneField = useRef<HTMLInputElement>(null)
  const domainField = useRef<HTMLInputElement>(null)
  const cfTokenField = useRef<TurnstileInstance>(null)

  const [responseType, setResponseType] = useState<ResponseType>('none')
  const [responseMessage, setResponseMessage] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleSubmit = async (e: React.FormEvent) => {
    if (isSubmitting) return
    e.preventDefault()
    setIsSubmitting(true)

    const cfToken = await cfTokenField.current?.getResponsePromise()
    if (!cfToken) {
      setResponseMessage('Please complete the captcha.')
      setResponseType('failure')
      setIsSubmitting(false)
      return
    }

    const data = {
      firstName: firstNameField.current?.value ?? '',
      lastName: lastNameField.current?.value ?? '',
      email: emailField.current?.value ?? '',
      domainName: domainField.current?.value ?? '',
      phone: phoneField.current?.value ?? '',
      cfToken,
    }

    try {
      const res = await fetch('/api/try-now', {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      const json = await res.json()

      setIsSubmitting(false)

      if (json.success) {
        if (firstNameField.current) firstNameField.current.value = ''
        if (lastNameField.current) lastNameField.current.value = ''
        if (emailField.current) emailField.current.value = ''
        if (domainField.current) domainField.current.value = ''
        if (phoneField.current) phoneField.current.value = ''
        router.push('/try/confirmation')
      } else {
        setResponseMessage(
          json.message ??
            'It seems something went wrong. You can email <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> instead.'
        )
        cfTokenField.current?.reset()
        setResponseType('failure')
      }
    } catch (err) {
      setIsSubmitting(false)
      cfTokenField.current?.reset()
      setResponseMessage(
        'Could not reach the server. You can email <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> instead.'
      )
      setResponseType('failure')
    }
  }

  return (
    <section className="relative overflow-hidden bg-surface pt-20 pb-24 lg:pt-28 lg:pb-32">
      <BackgroundField />
      <Container size="xl" className="relative">
        <div className="grid items-start gap-16 lg:grid-cols-[1fr_1.05fr] lg:gap-20">
          {/* Left: messaging */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Eyebrow tone="brand">Free 30-day trial</Eyebrow>
            </motion.div>
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
            >
              <Heading as="h1" size="display-xl">
                Spin up a fully-loaded
                <br />
                Monofor tenant.
              </Heading>
            </motion.div>
            <motion.div
              className="mt-6 max-w-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.12 }}
            >
              <Text size="xl" tone="secondary">
                Five minutes to your own environment. Every product included.
                No credit card.
              </Text>
            </motion.div>

            <ul className="mt-10 grid gap-3 sm:grid-cols-2">
              {highlightedFeatures.map((feature, i) => (
                <motion.li
                  key={feature}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.5,
                    ease: EASE,
                    delay: 0.15 + i * 0.05,
                  }}
                  className="flex items-center gap-3 text-body-md text-ink-secondary"
                >
                  <CheckDot />
                  {feature}
                </motion.li>
              ))}
            </ul>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.45,
                ease: EASE,
                delay: 0.15 + highlightedFeatures.length * 0.05,
              }}
              className="mt-10 flex items-center gap-2 text-body-sm text-ink-tertiary"
            >
              <span aria-hidden>🖖</span>
              And we don&apos;t need your credit card.
            </motion.div>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: EASE, delay: 0.08 }}
            className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                id="try-firstname"
                label="First name"
                required
                inputRef={firstNameField}
                defaultValue={defaultValues.firstName}
                type="text"
                placeholder="Jane"
                autoComplete="given-name"
                minLength={2}
              />
              <Field
                id="try-lastname"
                label="Last name"
                required
                inputRef={lastNameField}
                defaultValue={defaultValues.lastName}
                type="text"
                placeholder="Doe"
                autoComplete="family-name"
                minLength={2}
              />
            </div>
            <div className="mt-5">
              <Field
                id="try-email"
                label="Work email"
                required
                inputRef={emailField}
                defaultValue={defaultValues.email}
                type="email"
                placeholder="you@your-company.com"
                autoComplete="email"
              />
            </div>
            <div className="mt-5">
              <Field
                id="try-phone"
                label="Phone number"
                required
                inputRef={phoneField}
                defaultValue={defaultValues.phone}
                type="tel"
                placeholder="+1 415 800 4925"
                autoComplete="tel"
              />
            </div>

            <div className="mt-5">
              <label
                htmlFor="try-domain"
                className="block text-body-sm font-medium text-ink-primary"
              >
                Pick a domain name{' '}
                <span className="text-ink-tertiary">(required)</span>
              </label>
              <div className="mt-2 flex items-stretch overflow-hidden rounded-xl border border-line bg-surface focus-within:border-brand-700 focus-within:ring-2 focus-within:ring-brand-500/20">
                <span className="flex items-center bg-surface-subtle px-4 text-body-sm text-ink-tertiary">
                  https://
                </span>
                <input
                  id="try-domain"
                  ref={domainField}
                  defaultValue={defaultValues.domain}
                  name="domainName"
                  required
                  type="text"
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:outline-none"
                />
                <span className="flex items-center bg-surface-subtle px-4 text-body-sm text-ink-tertiary">
                  .{defaultValues.domainSuffix}
                </span>
              </div>
              <Text size="sm" tone="tertiary" className="mt-2">
                Lowercase letters, numbers, and dashes only. Must contain your
                email domain.
              </Text>
            </div>

            <Text size="sm" tone="tertiary" className="mt-6">
              By creating your account, you agree to the{' '}
              <Link
                href="/legal/terms-and-service"
                target="_blank"
                className="text-brand-900 underline-offset-4 hover:underline"
              >
                Terms and Conditions
              </Link>
              .
            </Text>

            {responseMessage && responseType === 'failure' && (
              <div
                className="mt-5 rounded-xl bg-[#fef2f2] px-4 py-3 text-body-sm text-[#b91c1c]"
                dangerouslySetInnerHTML={{ __html: responseMessage }}
              />
            )}

            <div className="mt-6">
              <Turnstile
                ref={cfTokenField}
                siteKey={cfTurnstileKey!}
                options={{ theme: 'light' }}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 inline-flex w-full items-center justify-center rounded-pill bg-brand-900 px-7 py-3.5 text-body-md font-medium text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Creating your account…' : 'Start free trial →'}
            </button>
          </motion.form>
        </div>
      </Container>
    </section>
  )
}

function Field({
  id,
  label,
  required,
  inputRef,
  defaultValue,
  type,
  placeholder,
  autoComplete,
  minLength,
}: {
  id: string
  label: string
  required?: boolean
  inputRef: React.RefObject<HTMLInputElement>
  defaultValue: string
  type: string
  placeholder: string
  autoComplete: string
  minLength?: number
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-body-sm font-medium text-ink-primary"
      >
        {label}{' '}
        {required && <span className="text-ink-tertiary">(required)</span>}
      </label>
      <input
        id={id}
        ref={inputRef}
        type={type}
        required={required}
        minLength={minLength}
        placeholder={placeholder}
        autoComplete={autoComplete}
        defaultValue={defaultValue}
        className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
      />
    </div>
  )
}

function CheckDot() {
  return (
    <svg
      className="h-5 w-5 flex-shrink-0"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="10" cy="10" r="10" fill="#25855c" fillOpacity="0.15" />
      <path
        d="M6 10.2 8.6 12.8 14 7.4"
        stroke="#25855c"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/70 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/60 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-try"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-try)" />
      </svg>
    </div>
  )
}
